/**
 * @fileoverview ClubOS Youth Academy — Players Management
 * MSC-YYYY-XXXX player codes, batch assignment, status tracking, detail view.
 * Changes: Scholarship 25%, admission fee tab (one-time, admin-unlock), PDF download,
 *          date-slider + status filters, full mandatory validation.
 */

import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { academyService } from "api";
import { useAuth } from "context/AuthContext";
import FootballLoader from "components/FootballLoader";

const STATUS_COLOR = { Active: "success", Inactive: "secondary", Trial: "warning" };
const FEE_PLAN_COLOR = { Standard: "info", "Scholarship (25%)": "warning", "Full Waiver": "success" };
const SEVERITY_COLOR = { Minor: "success", Moderate: "warning", Severe: "error" };
const ATTENDANCE_COLOR = { Present: "success", Late: "warning", Absent: "error", Leave: "info" };

const DEFAULT_FEE_PLANS = [
  { value: "Standard", label: "Standard" },
  { value: "Scholarship (25%)", label: "Scholarship (25%)" },
  { value: "Full Waiver", label: "Full Waiver" },
];

function loadFeePlans() {
  try {
    const raw = localStorage.getItem("clubos_academy_config");
    if (!raw) return DEFAULT_FEE_PLANS;
    const cfg = JSON.parse(raw);
    if (Array.isArray(cfg.feePlans) && cfg.feePlans.length > 0) return cfg.feePlans;
  } catch { /* fall through */ }
  return DEFAULT_FEE_PLANS;
}

const CURRENT_YEAR = new Date().getFullYear();

const emptyForm = {
  fullName: "", dateOfBirth: "", parentName: "", parentPhone: "", parentEmail: "",
  address: "", monthlyFee: "", feePlan: "Standard", batchId: "", status: "Active", notes: "",
};

const emptyAdmissionForm = {
  admissionFee: "", admissionDate: new Date().toISOString().slice(0, 10), admissionNotes: "",
};

// ── Validate player form ────────────────────────────────────────────────────────
function validatePlayerForm(form, admissionForm, admissionTab, admissionLocked) {
  const errs = {};
  if (!form.fullName.trim()) errs.fullName = "Full name is required.";
  if (!form.dateOfBirth) errs.dateOfBirth = "Date of birth is required.";
  if (!form.parentName.trim()) errs.parentName = "Parent/Guardian name is required.";
  if (!form.parentPhone.trim()) errs.parentPhone = "Parent phone is required.";
  else if (!/^\d{10}$/.test(form.parentPhone.replace(/\s/g, ""))) errs.parentPhone = "Enter a valid 10-digit phone number.";
  if (form.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)) errs.parentEmail = "Enter a valid email address.";
  if (!form.monthlyFee || Number(form.monthlyFee) < 0) errs.monthlyFee = "Monthly fee is required.";
  if (!form.batchId) errs.batchId = "Batch assignment is required.";
  if (!admissionLocked && admissionTab) {
    if (!admissionForm.admissionFee || Number(admissionForm.admissionFee) < 0) errs.admissionFee = "Admission fee is required.";
    if (!admissionForm.admissionDate) errs.admissionDate = "Admission date is required.";
  }
  return errs;
}

// ── Generate PDF report ─────────────────────────────────────────────────────────
async function downloadPlayersPDF(players, filterStatus, filterBatch, batches, yearRange, search) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const batchName = batches.find((b) => b.id === filterBatch)?.name ?? "All Batches";
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text("Academy Players Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);
  doc.text(`Filters — Status: ${filterStatus || "All"} | Batch: ${batchName} | Search: ${search || "None"}`, 14, 30);

  autoTable(doc, {
    startY: 36,
    head: [["#", "Player Code", "Full Name", "DOB", "Age Grp", "Batch", "Fee/Mo", "Plan", "Parent", "Phone", "Status"]],
    body: players.map((p, i) => [
      i + 1,
      p.playerCode,
      p.fullName,
      p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : "",
      p.ageGroup,
      p.batchName ?? "Unassigned",
      `₹${Number(p.monthlyFee).toLocaleString("en-IN")}`,
      p.feePlan ?? "Standard",
      p.parentName ?? "",
      p.parentPhone ?? "",
      p.status,
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 255] },
  });

  doc.save(`players-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Player row ─────────────────────────────────────────────────────────────────
function PlayerRow({ player, onEdit, onView }) {
  return (
    <SoftBox
      display="flex" alignItems="center" py={1} px={2}
      sx={{
        borderBottom: "1px solid #f5f5f5",
        cursor: "pointer",
        minWidth: 700,
        "&:hover": { backgroundColor: "rgba(26,115,232,0.03)" },
      }}
      onClick={() => onView(player)}
    >
      <SoftBox flex="0 0 115px">
        <SoftTypography variant="caption" fontWeight="bold" color="info" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {player.playerCode}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="1" display="flex" alignItems="center" gap={1} sx={{ overflow: "hidden" }}>
        <SoftTypography variant="button" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {player.fullName}
        </SoftTypography>
        <SoftTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          · {player.ageGroup} · Age {player.age} · {player.batchName ?? "Unassigned"}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          ₹{Number(player.monthlyFee).toLocaleString("en-IN")}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 130px" textAlign="center">
        <SoftBadge
          badgeContent={player.feePlan ?? "Standard"}
          color={FEE_PLAN_COLOR[player.feePlan] ?? "info"}
          size="xs" container
        />
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftBadge badgeContent={player.status} color={STATUS_COLOR[player.status] ?? "info"} size="xs" container />
      </SoftBox>
      <SoftBox flex="0 0 40px" textAlign="right">
        <SoftButton variant="text" color="info" iconOnly size="small"
          onClick={(e) => { e.stopPropagation(); onEdit(player); }}>
          <Icon>edit</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

PlayerRow.propTypes = {
  player: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

// ── Main component ─────────────────────────────────────────────────────────────
function AcademyPlayers() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin";

  const [players, setPlayers]           = useState([]);
  const [batches, setBatches]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBatch, setFilterBatch]   = useState("");
  const [yearRange, setYearRange]       = useState([CURRENT_YEAR - 5, CURRENT_YEAR]);

  const [feePlans, setFeePlans]             = useState(loadFeePlans);

  const [dialogOpen, setDialogOpen]         = useState(false);
  const [dialogTab, setDialogTab]           = useState(0);   // 0=Profile 1=AdmissionFee
  const [editing, setEditing]               = useState(null);
  const [form, setForm]                     = useState(emptyForm);
  const [admissionForm, setAdmissionForm]   = useState(emptyAdmissionForm);
  const [admissionLocked, setAdmissionLocked] = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [fieldErrors, setFieldErrors]       = useState({});
  const [saveError, setSaveError]           = useState("");

  // Detail view
  const [viewPlayer, setViewPlayer]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attendance, setAttendance]       = useState([]);
  const [injuries, setInjuries]           = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getPlayers({
        status: filterStatus || undefined,
        batchId: filterBatch || undefined,
        search: search || undefined,
      }),
      academyService.getBatches({ status: "Active" }),
    ])
      .then(([p, b]) => {
        setPlayers(Array.isArray(p) ? p : []);
        setBatches(Array.isArray(b) ? b : []);
      })
      .catch((err) => {
        console.error("Failed to load players:", err);
        setPlayers([]);
        setBatches([]);
      })
      .finally(() => setLoading(false));
  }, [filterStatus, filterBatch, search]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setFeePlans(loadFeePlans());
    setEditing(null);
    setForm(emptyForm);
    setAdmissionForm(emptyAdmissionForm);
    setAdmissionLocked(false);
    setFieldErrors({});
    setSaveError("");
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openEdit = (player) => {
    setFeePlans(loadFeePlans());
    setEditing(player);
    setForm({
      fullName: player.fullName,
      dateOfBirth: player.dateOfBirth ? String(player.dateOfBirth).slice(0, 10) : "",
      parentName: player.parentName ?? "",
      parentPhone: player.parentPhone ?? "",
      parentEmail: player.parentEmail ?? "",
      address: player.address ?? "",
      monthlyFee: player.monthlyFee,
      feePlan: player.feePlan ?? "Standard",
      batchId: player.batchId ?? "",
      status: player.status ?? "Active",
      notes: player.notes ?? "",
    });
    // Admission fee: lock if already recorded
    const hasAdmission = player.admissionFee != null && player.admissionFee !== "";
    setAdmissionForm({
      admissionFee: player.admissionFee ?? "",
      admissionDate: player.admissionDate ? String(player.admissionDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
      admissionNotes: player.admissionNotes ?? "",
    });
    setAdmissionLocked(hasAdmission);
    setFieldErrors({});
    setSaveError("");
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openView = async (player) => {
    setViewPlayer(player);
    setDetailLoading(true);
    setAttendance([]);
    setInjuries([]);
    try {
      const [att, inj] = await Promise.all([
        academyService.getAttendance({ playerId: player.id }),
        academyService.getInjuries({ playerId: player.id }),
      ]);
      setAttendance(Array.isArray(att) ? att.slice(0, 10) : []);
      setInjuries(Array.isArray(inj) ? inj : []);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const f = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const af = (key) => (e) => {
    setAdmissionForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    const errs = validatePlayerForm(form, admissionForm, dialogTab === 1, admissionLocked);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted errors before saving.");
      // Switch to the tab that has errors
      if (errs.admissionFee || errs.admissionDate) setDialogTab(1);
      else setDialogTab(0);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        ...form,
        monthlyFee: Number(form.monthlyFee) || 0,
        batchId: form.batchId || null,
        dateOfBirth: form.dateOfBirth,
        admissionDate: admissionForm.admissionDate || new Date().toISOString().slice(0, 10),
        admissionFee: admissionLocked ? undefined : (Number(admissionForm.admissionFee) || 0),
        admissionNotes: admissionLocked ? undefined : admissionForm.admissionNotes,
      };
      if (editing) {
        await academyService.updatePlayer(editing.id, payload);
      } else {
        await academyService.createPlayer(payload);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      setSaveError(e?.detail ?? e?.title ?? "Save failed — check console for details.");
    } finally {
      setSaving(false);
    }
  };

  const handleFeePlanChange = async (player, newPlan) => {
    await academyService.updatePlayer(player.id, { ...player, feePlan: newPlan });
    load();
  };

  const handleDownloadPDF = () => {
    downloadPlayersPDF(players, filterStatus, filterBatch, batches, yearRange, search);
  };

  const active   = players.filter((p) => p.status === "Active").length;
  const trial    = players.filter((p) => p.status === "Trial").length;
  const inactive = players.filter((p) => p.status === "Inactive").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Academy Players</SoftTypography>
            <SoftTypography variant="body2" color="text">Click a player to view attendance &amp; injury history</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="info" startIcon={<Icon>download</Icon>} onClick={handleDownloadPDF}>
              Download Report
            </SoftButton>
            <SoftButton variant="gradient" color="info" startIcon={<Icon>person_add</Icon>} onClick={openAdd}>
              Add Player
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPI row */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Players" }} count={String(players.length)}
                percentage={{ color: "info", text: "registered" }} icon={{ color: "info", component: "groups" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Active" }} count={String(active)}
                percentage={{ color: "success", text: "training" }} icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "On Trial" }} count={String(trial)}
                percentage={{ color: "warning", text: "evaluation" }} icon={{ color: "warning", component: "person_search" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Inactive" }} count={String(inactive)}
                percentage={{ color: "secondary", text: "inactive" }} icon={{ color: "secondary", component: "person_off" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Filters */}
        <Card>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small" placeholder="Search by name, code, or parent…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon fontSize="small">search</Icon></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <TextField select size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">◈ All Status</MenuItem>
              <MenuItem value="Active">▲ Active</MenuItem>
              <MenuItem value="Trial">▶ Trial</MenuItem>
              <MenuItem value="Inactive">▼ Inactive</MenuItem>
            </TextField>
            <TextField select size="small" label="Batch" value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Batches</MenuItem>
              {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>

            {/* Year range inputs */}
            <TextField select size="small" label="Year From" value={yearRange[0]}
              onChange={(e) => setYearRange([Number(e.target.value), yearRange[1]])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Year To" value={yearRange[1]}
              onChange={(e) => setYearRange([yearRange[0], Number(e.target.value)])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>

            {(filterStatus || filterBatch || search) && (
              <Chip
                label="Clear filters"
                size="small"
                onDelete={() => { setFilterStatus(""); setFilterBatch(""); setSearch(""); }}
                sx={{ height: 28 }}
              />
            )}
            <SoftTypography variant="caption" color="text" ml="auto">
              {players.length} players found
            </SoftTypography>
          </SoftBox>

          {/* Scrollable table area */}
          <SoftBox sx={{ overflowX: "auto" }}>
            {/* Column headers */}
            <SoftBox display="flex" alignItems="center" px={2} py={1}
              sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", minWidth: 700 }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 115px" }}>ID CODE</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>PLAYER</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>FEE/MO</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px", textAlign: "center" }}>PLAN</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>STATUS</SoftTypography>
              <SoftBox sx={{ flex: "0 0 40px" }} />
            </SoftBox>

            {loading ? (
              <FootballLoader label="Loading players…" />
            ) : players.length === 0 ? (
              <SoftBox p={4} textAlign="center">
                <SoftTypography variant="caption" color="text">No players found</SoftTypography>
              </SoftBox>
            ) : (
              players.map((p) => (
                <PlayerRow key={p.id} player={p} onEdit={openEdit} onView={openView} />
              ))
            )}
          </SoftBox>
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Player" : "Add Academy Player"}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {saveError && <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{saveError}</Alert>}

          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: "1px solid #f0f0f0" }}>
            <Tab label="Player Profile" />
            <Tab label="Admission Fee" />
          </Tabs>

          {/* ── Profile Tab ── */}
          {dialogTab === 0 && (
            <Grid container spacing={2} sx={{ p: 3, pt: 2 }}>
              <Grid item xs={12}>
                <TextField fullWidth required label="Full Name" value={form.fullName}
                  onChange={f("fullName")} size="small"
                  error={Boolean(fieldErrors.fullName)} helperText={fieldErrors.fullName} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth required label="Date of Birth" type="date" value={form.dateOfBirth}
                  onChange={f("dateOfBirth")} size="small" InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldErrors.dateOfBirth)} helperText={fieldErrors.dateOfBirth} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth required label="Monthly Fee (₹)" type="number" value={form.monthlyFee}
                  onChange={f("monthlyFee")} size="small"
                  error={Boolean(fieldErrors.monthlyFee)} helperText={fieldErrors.monthlyFee} />
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth required label="Fee Plan" value={form.feePlan}
                  onChange={f("feePlan")} size="small">
                  {feePlans.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth required label="Batch" value={form.batchId}
                  onChange={f("batchId")} size="small"
                  error={Boolean(fieldErrors.batchId)} helperText={fieldErrors.batchId}>
                  <MenuItem value="">Unassigned</MenuItem>
                  {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name} ({b.ageGroup})</MenuItem>)}
                </TextField>
              </Grid>
              {editing && (
                <Grid item xs={6}>
                  <TextField select fullWidth label="Status" value={form.status}
                    onChange={f("status")} size="small">
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Trial">Trial</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              )}
              <Grid item xs={editing ? 6 : 12}>
                <TextField fullWidth required label="Parent / Guardian Name" value={form.parentName}
                  onChange={f("parentName")} size="small"
                  error={Boolean(fieldErrors.parentName)} helperText={fieldErrors.parentName} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth required label="Parent Phone" value={form.parentPhone}
                  onChange={f("parentPhone")} size="small"
                  error={Boolean(fieldErrors.parentPhone)} helperText={fieldErrors.parentPhone} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Parent Email" value={form.parentEmail}
                  onChange={f("parentEmail")} size="small"
                  error={Boolean(fieldErrors.parentEmail)} helperText={fieldErrors.parentEmail} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" value={form.address}
                  onChange={f("address")} size="small" multiline rows={2} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" value={form.notes}
                  onChange={f("notes")} size="small" multiline rows={2} />
              </Grid>
            </Grid>
          )}

          {/* ── Admission Fee Tab ── */}
          {dialogTab === 1 && (
            <SoftBox p={3}>
              {admissionLocked ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Admission fee is locked after initial entry.
                    {isAdmin && (
                      <SoftButton size="small" color="warning" variant="text"
                        onClick={() => setAdmissionLocked(false)} sx={{ ml: 1, minWidth: 0 }}>
                        <Icon fontSize="small">lock_open</Icon> Unlock
                      </SoftButton>
                    )}
                  </Alert>
                  <SoftBox display="flex" gap={3} flexWrap="wrap">
                    <SoftBox>
                      <SoftTypography variant="caption" color="text">Admission Fee</SoftTypography>
                      <SoftTypography variant="h6" fontWeight="bold" color="dark">
                        ₹{Number(admissionForm.admissionFee).toLocaleString("en-IN")}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox>
                      <SoftTypography variant="caption" color="text">Date</SoftTypography>
                      <SoftTypography variant="h6" fontWeight="bold" color="dark">
                        {admissionForm.admissionDate}
                      </SoftTypography>
                    </SoftBox>
                    {admissionForm.admissionNotes && (
                      <SoftBox flex={1}>
                        <SoftTypography variant="caption" color="text">Notes</SoftTypography>
                        <SoftTypography variant="body2" color="dark">{admissionForm.admissionNotes}</SoftTypography>
                      </SoftBox>
                    )}
                  </SoftBox>
                </>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Admission fee is a one-time charge. Once saved, only Admin can unlock to edit.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField fullWidth required label="Admission Fee (₹)" type="number"
                        value={admissionForm.admissionFee} onChange={af("admissionFee")} size="small"
                        error={Boolean(fieldErrors.admissionFee)} helperText={fieldErrors.admissionFee} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth required label="Admission Date" type="date"
                        value={admissionForm.admissionDate} onChange={af("admissionDate")} size="small"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldErrors.admissionDate)} helperText={fieldErrors.admissionDate} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Notes" value={admissionForm.admissionNotes}
                        onChange={af("admissionNotes")} size="small" multiline rows={2} />
                    </Grid>
                  </Grid>
                </>
              )}
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Add Player"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Player detail dialog ── */}
      <Dialog open={Boolean(viewPlayer)} onClose={() => setViewPlayer(null)} maxWidth="sm" fullWidth>
        {viewPlayer && (
          <>
            <DialogTitle>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">{viewPlayer.fullName}</SoftTypography>
                  <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                    {viewPlayer.playerCode} · {viewPlayer.batchName ?? "Unassigned"}
                  </SoftTypography>
                </SoftBox>
                <SoftBadge badgeContent={viewPlayer.status} color={STATUS_COLOR[viewPlayer.status] ?? "info"} size="xs" container />
              </SoftBox>
            </DialogTitle>
            <DialogContent>
              {detailLoading ? (
                <SoftBox py={3} textAlign="center">
                  <SoftTypography variant="caption" color="text">Loading…</SoftTypography>
                </SoftBox>
              ) : (
                <>
                  {/* Active injuries */}
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                    INJURIES
                  </SoftTypography>
                  {injuries.length === 0 ? (
                    <SoftTypography variant="caption" color="text">No injury records.</SoftTypography>
                  ) : (
                    injuries.map((inj) => (
                      <SoftBox key={inj.id} display="flex" justifyContent="space-between" alignItems="center"
                        py={1} sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftBox>
                          <SoftTypography variant="caption" fontWeight="medium" color="dark">
                            {inj.injuryType} — {inj.bodyPart}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">
                            {String(inj.injuryDate)} · {inj.physioNotes ?? ""}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" gap={0.5}>
                          <SoftBadge badgeContent={inj.severity} color={SEVERITY_COLOR[inj.severity] ?? "info"} size="xs" container />
                          <SoftBadge badgeContent={inj.recoveryStatus}
                            color={inj.recoveryStatus === "Cleared" ? "success" : inj.recoveryStatus === "Recovering" ? "warning" : "error"}
                            size="xs" container />
                        </SoftBox>
                      </SoftBox>
                    ))
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Last 10 attendance */}
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                    RECENT ATTENDANCE (last 10)
                  </SoftTypography>
                  {attendance.length === 0 ? (
                    <SoftTypography variant="caption" color="text">No attendance records.</SoftTypography>
                  ) : (
                    attendance.map((a) => (
                      <SoftBox key={a.id} display="flex" justifyContent="space-between" alignItems="center"
                        py={0.75} sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftTypography variant="caption" color="text">
                          {String(a.attendanceDate)} · {a.batchName}
                        </SoftTypography>
                        <SoftBadge badgeContent={a.attendanceStatus} color={ATTENDANCE_COLOR[a.attendanceStatus] ?? "info"} size="xs" container />
                      </SoftBox>
                    ))
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <SoftButton variant="text" color="secondary" onClick={() => setViewPlayer(null)}>Close</SoftButton>
              <SoftButton variant="gradient" color="info" onClick={() => { setViewPlayer(null); openEdit(viewPlayer); }}>
                Edit Player
              </SoftButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyPlayers;
